/// <reference path="../../../types/axios-fix.d.ts" />
import { BaseService, httpClientService, authInterceptorService, errorHandlerService, cacheService } from '../core';
import { PaymentMethod } from './subscription.service';

/**
 * Payment Setup Intent interface
 */
export interface PaymentSetupIntent {
  clientSecret: string;
  publishableKey?: string;
}

/**
 * Checkout Session interface
 */
export interface CheckoutSession {
  sessionId: string;
  url?: string;
  publishableKey?: string;
}

/**
 * Customer Portal Session interface
 */
export interface CustomerPortalSession {
  url: string;
  sessionId?: string;
}

/**
 * Billing Details interface
 */
export interface BillingDetails {
  paymentMethod: string;
  cardNumber?: string;
  expiryDate?: string;
  cvc?: string;
  billingAddress?: string;
  name?: string;
  email?: string;
  phone?: string;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  };
}

/**
 * Payment Service
 * 
 * Handles all payment-related operations including:
 * - Payment method management
 * - Checkout sessions
 * - Customer portal sessions
 * - Payment setup intents
 * - Billing details management
 * 
 * Extracted from the monolithic API service.
 */
export class PaymentService extends BaseService {
  constructor() {
    super(httpClientService, authInterceptorService, errorHandlerService, cacheService);
  }

  // ===== PAYMENT METHOD OPERATIONS =====

  /**
   * Create a setup intent for adding a payment method
   */
  async createSetupIntent(organizationId: string): Promise<PaymentSetupIntent> {
    try {
      console.log(`Creating setup intent for organization ${organizationId}`);
      
      const response = await this.post<any>(
        `/organizations/${organizationId}/payment/setup-intent`
      );
      
      return {
        clientSecret: response.clientSecret || response.client_secret,
        publishableKey: response.publishableKey || response.publishable_key
      };
    } catch (error) {
      console.error(`Error creating setup intent for organization ${organizationId}:`, error);
      throw this.errorHandler.handleError(error, `PaymentService.createSetupIntent(${organizationId})`);
    }
  }

  /**
   * Add a payment method to an organization
   */
  async addPaymentMethod(
    organizationId: string,
    paymentToken: string,
    setAsDefault: boolean = false
  ): Promise<{ paymentMethod: PaymentMethod }> {
    try {
      console.log(`Adding payment method to organization ${organizationId}`);

      const response = await this.post<any>(
        `/organizations/${organizationId}/payment-methods`,
        {
          payment_token: paymentToken,
          set_as_default: setAsDefault
        }
      );

      // Invalidate cache
      this.invalidateCache('organization');

      return {
        paymentMethod: this.normalizePaymentMethod(response.data?.paymentMethod || response.paymentMethod || response)
      };
    } catch (error) {
      console.error(`Error adding payment method to organization ${organizationId}:`, error);
      throw this.errorHandler.handleError(error, `PaymentService.addPaymentMethod(${organizationId})`);
    }
  }

  /**
   * List payment methods for an organization
   */
  async listPaymentMethods(organizationId: string): Promise<PaymentMethod[]> {
    try {
      console.log(`Fetching payment methods for organization ${organizationId}`);

      const response = await this.getCached<any>(
        `/organizations/${organizationId}/payment-methods`
      );

      const paymentMethods = response.data?.paymentMethods || response.paymentMethods || [];
      return paymentMethods.map(this.normalizePaymentMethod);
    } catch (error) {
      console.error(`Error fetching payment methods for organization ${organizationId}:`, error);
      throw this.errorHandler.handleError(error, `PaymentService.listPaymentMethods(${organizationId})`);
    }
  }

  /**
   * Set default payment method
   */
  async setDefaultPaymentMethod(
    paymentMethodId: string
  ): Promise<{ paymentMethod: PaymentMethod }> {
    try {
      console.log(`Setting default payment method ${paymentMethodId}`);

      const response = await this.patch<any>(
        `/payment-methods/${paymentMethodId}/set-default`,
        {}
      );

      // Invalidate cache
      this.invalidateCache('organization');

      return {
        paymentMethod: this.normalizePaymentMethod(response.data?.paymentMethod || response.paymentMethod || response)
      };
    } catch (error) {
      console.error(`Error setting default payment method ${paymentMethodId}:`, error);
      throw this.errorHandler.handleError(error, `PaymentService.setDefaultPaymentMethod(${paymentMethodId})`);
    }
  }

  /**
   * Remove a payment method
   */
  async removePaymentMethod(
    paymentMethodId: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      console.log(`Removing payment method ${paymentMethodId}`);

      const response = await this.delete<any>(
        `/payment-methods/${paymentMethodId}`
      );

      // Invalidate cache
      this.invalidateCache('organization');

      return {
        success: response.data?.success !== false,
        message: response.data?.message || response.message || 'Payment method removed successfully'
      };
    } catch (error) {
      console.error(`Error removing payment method ${paymentMethodId}:`, error);
      throw this.errorHandler.handleError(error, `PaymentService.removePaymentMethod(${paymentMethodId})`);
    }
  }

  // ===== CHECKOUT & PORTAL OPERATIONS =====

  /**
   * Create a checkout session for purchasing a subscription
   */
  async createCheckoutSession(
    organizationId: string,
    planId: string,
    successUrl: string,
    cancelUrl: string
  ): Promise<CheckoutSession> {
    try {
      console.log(`Creating checkout session for organization ${organizationId}`);
      
      const response = await this.post<any>(
        `/organizations/${organizationId}/payment/checkout`,
        {
          planId,
          successUrl,
          cancelUrl
        }
      );
      
      return {
        sessionId: response.sessionId || response.session_id,
        url: response.url || response.checkout_url,
        publishableKey: response.publishableKey || response.publishable_key
      };
    } catch (error) {
      console.error(`Error creating checkout session for organization ${organizationId}:`, error);
      throw this.errorHandler.handleError(error, `PaymentService.createCheckoutSession(${organizationId})`);
    }
  }

  /**
   * Create a customer portal session for managing billing
   */
  async createCustomerPortalSession(
    organizationId: string,
    returnUrl: string
  ): Promise<CustomerPortalSession> {
    try {
      console.log(`Creating customer portal session for organization ${organizationId}`);
      
      const response = await this.post<any>(
        `/organizations/${organizationId}/payment/portal`,
        { returnUrl }
      );
      
      return {
        url: response.url || response.portal_url,
        sessionId: response.sessionId || response.session_id
      };
    } catch (error) {
      console.error(`Error creating customer portal session for organization ${organizationId}:`, error);
      throw this.errorHandler.handleError(error, `PaymentService.createCustomerPortalSession(${organizationId})`);
    }
  }

  // ===== BILLING OPERATIONS =====

  /**
   * Update billing details for a subscription
   */
  async updateSubscriptionBilling(
    subscriptionId: string,
    billingDetails: BillingDetails
  ): Promise<{ success: boolean; message: string }> {
    try {
      console.log(`Updating billing for subscription ${subscriptionId}`);
      
      await this.put(
        `/subscriptions/${subscriptionId}/billing`,
        billingDetails
      );
      
      // Invalidate cache
      this.invalidateCache('organization');
      
      return {
        success: true,
        message: 'Billing details updated successfully'
      };
    } catch (error) {
      console.error(`Error updating billing for subscription ${subscriptionId}:`, error);
      throw this.errorHandler.handleError(error, `PaymentService.updateSubscriptionBilling(${subscriptionId})`);
    }
  }

  /**
   * Update organization billing details
   */
  async updateOrganizationBillingDetails(
    organizationId: string,
    billingDetails: BillingDetails
  ): Promise<{ success: boolean; message: string }> {
    try {
      console.log(`Updating billing details for organization ${organizationId}`);
      
      await this.put(
        `/organizations/${organizationId}/billing`,
        billingDetails
      );
      
      // Invalidate cache
      this.invalidateCache('organization');
      
      return {
        success: true,
        message: 'Billing details updated successfully'
      };
    } catch (error) {
      console.error(`Error updating billing details for organization ${organizationId}:`, error);
      throw this.errorHandler.handleError(error, `PaymentService.updateOrganizationBillingDetails(${organizationId})`);
    }
  }

  // ===== HELPER METHODS =====

  /**
   * Normalize payment method data from API response
   */
  private normalizePaymentMethod(pmData: any): PaymentMethod {
    return {
      id: pmData.id || pmData._id,
      type: pmData.type || 'card',
      brand: pmData.brand || pmData.card?.brand,
      last4: pmData.last4 || pmData.lastFour || pmData.card?.last4 || pmData.card?.lastFour || '****',
      expiryMonth: pmData.expiryMonth || pmData.expMonth || pmData.expiry_month || pmData.card?.exp_month || pmData.card?.expMonth,
      expiryYear: pmData.expiryYear || pmData.expYear || pmData.expiry_year || pmData.card?.exp_year || pmData.card?.expYear,
      isDefault: pmData.isDefault !== undefined ? pmData.isDefault : pmData.is_default,
      billingDetails: pmData.billingDetails || pmData.billing_details
    };
  }
}

// Export singleton instance
export const paymentService = new PaymentService();